import { Component, Input, OnDestroy } from '@angular/core';
import { UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';

import { FormCoreComponent } from '../form-core';
import { Subscription } from 'rxjs';
import {
  CARER_ROLE,
  COORDINATOR_ROLE,
  CUSTOMER_MEMBER_ROLE,
  FAMILY_ROLE,
  ORGANISATION_ADMIN_ROLE,
} from '@careapp-group/core';
import { OrganisationPreferenceDto } from '@careapp-group/ng-sdk';

@Component({
    selector: 'organisation-preferences-form',
    templateUrl: './organisation-preferences-form.component.html',
    styleUrls: ['./organisation-preferences-form.component.scss'],
    standalone: false
})
export class OrganisationPreferencesFormComponent
  extends FormCoreComponent<OrganisationPreferenceDto>
  implements OnDestroy
{
  @Input() set readOnly(value: boolean) {
    this._readOnly = value;
    if (!!value && this.form.enabled) {
      this.form.disable();
      this.shareCheckIns.disable();
    }
  }
  @Input()
  set preferences(value: OrganisationPreferenceDto) {
    if (!!value) {
      let newValue = { ...value };

      if (newValue.inviteToCustomerRoles == null) {
        newValue.inviteToCustomerRoles = this.defaultSaveImageRoles;
      }
      if (newValue.saveImageRoles == null) {
        newValue.saveImageRoles = this.defaultSaveImageRoles;
      }
      if (newValue.shareToPublicRoles == null) {
        newValue.shareToPublicRoles = this.defaultShareToPublicRoles;
      }
      this.form.patchValue(newValue, { emitEvent: false, onlySelf: true });
      this.updateSaveImageRoles(ORGANISATION_ADMIN_ROLE, true);
      this.updateShareCheckIns(newValue);
    }
  }

  shareCheckIns = new UntypedFormControl(false);
  subs: Subscription[] = [];

  ORGANISATION_ADMIN_ROLE = ORGANISATION_ADMIN_ROLE;
  defaultInviteToCustomerRoles: string[] = [ORGANISATION_ADMIN_ROLE, COORDINATOR_ROLE];
  defaultSaveImageRoles: string[] = [ORGANISATION_ADMIN_ROLE, COORDINATOR_ROLE];
  defaultShareToPublicRoles: string[] = [ORGANISATION_ADMIN_ROLE, COORDINATOR_ROLE];
  userRoles: string[] = [COORDINATOR_ROLE, CARER_ROLE, FAMILY_ROLE, CUSTOMER_MEMBER_ROLE];

  _readOnly: boolean = false;

  constructor() {
    super();
    this.form = new UntypedFormGroup({
      severeConcernWarning: new UntypedFormControl(false, Validators.required),
      severeConcernMessage: new UntypedFormControl(false),
      autoShareLow: new UntypedFormControl(false, Validators.required),
      autoShareMid: new UntypedFormControl(false, Validators.required),
      autoShareHigh: new UntypedFormControl(false, Validators.required),
      autoShareSevere: new UntypedFormControl(false, Validators.required),
      minCheckInShareScore: new UntypedFormControl(-1, Validators.required),
      maxCheckInShareScore: new UntypedFormControl(-1, Validators.required),
      inviteToCustomerRoles: new UntypedFormControl(this.defaultInviteToCustomerRoles),
      saveImageRoles: new UntypedFormControl(this.defaultSaveImageRoles),
      shareToPublicRoles: new UntypedFormControl(this.defaultShareToPublicRoles),
      commentsRequireApproval: new UntypedFormControl(true),
      familyCanPostContent: new UntypedFormControl(false),
    });
    this.subs.push(
      this.shareCheckIns.valueChanges.subscribe((share) => this.updateMinMaxCheckIns())
    );
    this.subs.push(
      this.form.valueChanges.subscribe((changes) => this.updateShareCheckIns(this.form.value))
    );
    this.form.valueChanges.subscribe((changes) => {
      if (this._readOnly && this.form.enabled) {
        this.form.disable();
        this.shareCheckIns.disable();
      }
    });
  }

  // Depending on 'isAdding', will add if !exists, or remove if exists.
  updateRolesInArray(array: string[], role: string, isAdding: boolean): string[] {
    if (isAdding && array.indexOf(role) === -1) {
      return [...array, role];
      // Add this role if it does not exist already.
    } else if (!isAdding && array.indexOf(role) !== -1) {
      // Remove this role, if it exists.
      return [...array].filter((r) => r !== role);
    }
    return array;
  }

  updateInviteToCustomerRoles(role: string, checked: boolean) {
    let formRolesArray: string[] = this.form.get('inviteToCustomerRoles').value ?? [];
    this.form
      .get('inviteToCustomerRoles')
      .patchValue(this.updateRolesInArray(formRolesArray, role, checked));
  }

  updateSaveImageRoles(role: string, checked: boolean) {
    let formRolesArray: string[] = this.form.get('saveImageRoles').value ?? [];
    let newRoles = this.updateRolesInArray(formRolesArray, role, checked);
    this.form.get('saveImageRoles').patchValue(newRoles);
  }

  updateShareToPublicRoles(role: string, checked: boolean) {
    let formRolesArray: string[] = this.form.get('shareToPublicRoles').value ?? [];
    let newRoles = this.updateRolesInArray(formRolesArray, role, checked);
    this.form.get('shareToPublicRoles').patchValue(newRoles);
  }

  updateMinMaxCheckIns() {
    const opts = { emitEvent: false, onlySelf: true };
    const share = this.shareCheckIns.value;
    if (share) {
      this.form.patchValue(
        {
          minCheckInShareScore: 0,
          maxCheckInShareScore: 3,
        },
        opts
      );
    } else {
      this.form.patchValue(
        {
          minCheckInShareScore: -1,
          maxCheckInShareScore: -1,
        },
        opts
      );
    }
  }

  updateShareCheckIns(formValue) {
    const opts = { emitEvent: false, onlySelf: true };
    const values = {
      minCheckInShareScore: formValue.minCheckInShareScore,
      maxCheckInShareScore: formValue.maxCheckInShareScore,
    };
    // Prevent values from getting out of whack (min > max or max > min)
    if (values.minCheckInShareScore > values.maxCheckInShareScore) {
      values.minCheckInShareScore = values.maxCheckInShareScore;
    }
    if (values.maxCheckInShareScore < values.minCheckInShareScore) {
      values.maxCheckInShareScore = values.minCheckInShareScore;
    }
    this.form.patchValue(values, opts);

    // Update the toggle slider (if server returns -1 for either value)
    if (formValue.maxCheckInShareScore === -1) {
      this.shareCheckIns.patchValue(false, opts);
    } else {
      this.shareCheckIns.patchValue(true, opts);
    }
  }

  ngOnDestroy() {
    this.subs.forEach((sub) => sub.unsubscribe());
  }
}
